import type { Submission } from '@fyf/shared';
import { BRAND_COPY } from '@fyf/shared';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getSubmission } from '../api/client.js';
import { FlameCard } from '../components/FlameCard.js';
import { ScreenLayout } from '../components/ScreenLayout.js';

const { reveal } = BRAND_COPY.screens;

export function FlamePage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uuid) {
      setError('No flame ID provided.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      try {
        const data = await getSubmission(uuid!);
        if (!cancelled) {
          setSubmission(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load flame.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (loading) {
    return (
      <ScreenLayout>
        <p className="text-gray-300">Loading your flame...</p>
      </ScreenLayout>
    );
  }

  if (error) {
    return (
      <ScreenLayout>
        <h2 className="text-2xl font-serif text-center mb-4 text-red-400">Something went wrong.</h2>
        <p className="text-gray-300 text-center">{error}</p>
      </ScreenLayout>
    );
  }

  if (!submission) {
    return (
      <ScreenLayout>
        <h2 className="text-2xl font-serif text-center mb-4">Flame not found.</h2>
      </ScreenLayout>
    );
  }

  if (submission.status === 'generating') {
    return (
      <ScreenLayout>
        <h2 className="text-2xl font-serif text-center mb-4">Your flame is still being created.</h2>
        <p className="text-gray-300 text-center">Check back in a moment.</p>
      </ScreenLayout>
    );
  }

  if (submission.status === 'failed') {
    return (
      <ScreenLayout>
        <h2 className="text-2xl font-serif text-center mb-4 text-red-400">
          Flame generation failed.
        </h2>
        <p className="text-gray-300 text-center">Please visit the Aduro booth for assistance.</p>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout glowIntensity="high">
      <h1 className="text-[32px] font-['Lora',serif] text-center mb-6">
        This is <span className="text-brand-gradient italic">your</span> fire.
      </h1>

      {submission.flameImageUrl && (
        <FlameCard imageUrl={submission.flameImageUrl} displayName={submission.displayName} />
      )}

      <div className="mt-8 text-center">
        <h3 className="text-2xl font-['Lora',serif] text-brand-gradient mb-2">
          {reveal.muralCallout}
        </h3>
        <p className="font-['DM_Sans',sans-serif] text-base text-gray-300 max-w-md mb-2">
          {reveal.muralText}
        </p>
        <p className="font-['DM_Sans',sans-serif] text-base text-gray-300 max-w-md">
          {reveal.notebookPrompt}
        </p>
      </div>
    </ScreenLayout>
  );
}
